/**
 * PATH: packages/tenant-shared/src/settings/SignatureField.tsx
 * FILE_ID: TENANTSHARED-EMAILSIG-FIELD-001
 * PURPOSE: Shared WYSIWYG editor for the email signature, used by every
 *          Settings surface (universal modal @quotech/ui + the settings
 *          hub). A contentEditable surface so a multi-line / pasted
 *          signature keeps its formatting — replaces the plain <textarea>
 *          that expected raw text and silently lost line breaks/styling.
 * WHY:     The signature must be authored the same way wherever you set
 *          it up. Lives in tenant-shared (the lowest shared package) so
 *          @quotech/ui can consume it without importing email-hub
 *          (which would be a circular dependency).
 * FLOW:    valueHtml → contentEditable (edit / paste / format) → onChangeHtml
 * DEPENDENCIES:
 *   - lucide-react: toolbar icons
 * RELATED:
 *   - settings/emailSignature.ts: the canonical store this edits
 *   - packages/email-hub/.../RichTextEditor.tsx: the composer's richer
 *     sibling editor (same contentEditable approach, more controls)
 */
import { useCallback, useEffect, useRef } from 'react'
import { Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, RemoveFormatting } from 'lucide-react'

export interface SignatureFieldProps {
  /** Current signature as HTML. */
  valueHtml: string
  /** Fired on every edit with the new HTML. */
  onChangeHtml: (html: string) => void
  placeholder?: string
  ariaLabel?: string
  className?: string
}

/** I→O signature editor: valueHtml → (edits) → onChangeHtml(html). */
export function SignatureField({
  valueHtml,
  onChangeHtml,
  placeholder = 'e.g. Jake Smith\nAll Styled Steel — 0400 000 000',
  ariaLabel = 'Email signature',
  className,
}: SignatureFieldProps) {
  const editorRef = useRef<HTMLDivElement | null>(null)

  // Sync external value → DOM only when it diverges (load / external
  // reset). We never rewrite innerHTML on each keystroke — that would
  // drop the caret. The mount run seeds the initial value.
  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    if ((el.innerHTML || '') !== (valueHtml || '')) {
      el.innerHTML = valueHtml || ''
    }
  }, [valueHtml])

  const emit = useCallback(() => {
    const el = editorRef.current
    if (el) onChangeHtml(el.innerHTML)
  }, [onChangeHtml])

  const exec = useCallback((cmd: string, val?: string) => {
    const el = editorRef.current
    if (!el) return
    el.focus()
    document.execCommand(cmd, false, val)
    onChangeHtml(el.innerHTML)
  }, [onChangeHtml])

  const handleLink = useCallback(() => {
    const url = (typeof window !== 'undefined' ? window.prompt('Link URL', 'https://') : '') || ''
    const trimmed = url.trim()
    if (!trimmed || trimmed === 'https://') return
    const safe = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    exec('createLink', safe)
  }, [exec])

  const isEmpty = (valueHtml || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim().length === 0

  return (
    <div className={className}>
      <div className="flex items-center gap-0.5 mb-1.5 pb-1.5 border-b border-border">
        <ToolbarBtn icon={Bold} title="Bold" onClick={() => exec('bold')} />
        <ToolbarBtn icon={Italic} title="Italic" onClick={() => exec('italic')} />
        <ToolbarBtn icon={UnderlineIcon} title="Underline" onClick={() => exec('underline')} />
        <ToolbarBtn icon={LinkIcon} title="Insert link" onClick={handleLink} />
        <ToolbarBtn icon={RemoveFormatting} title="Clear formatting" onClick={() => exec('removeFormat')} />
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        data-empty={isEmpty ? 'true' : 'false'}
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        spellCheck
        className={
          'relative min-h-[120px] w-full rounded-lg border border-border bg-card px-3 py-2 '
          + 'text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/40 '
          + 'whitespace-pre-wrap break-words '
          + '[&[data-empty=true]:before]:content-[attr(data-placeholder)] '
          + '[&[data-empty=true]:before]:text-muted-foreground '
          + '[&[data-empty=true]:before]:pointer-events-none '
          + '[&[data-empty=true]:before]:absolute '
          + '[&[data-empty=true]:before]:top-2 '
          + '[&[data-empty=true]:before]:left-3 '
          + '[&[data-empty=true]:before]:right-3 '
          + '[&[data-empty=true]:before]:whitespace-pre-line'
        }
      />
    </div>
  )
}

function ToolbarBtn({
  icon: Icon,
  title,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      // onMouseDown (not onClick) so the button never steals the caret
      // selection from the editor before execCommand runs.
      onMouseDown={(e) => { e.preventDefault(); onClick() }}
      title={title}
      className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}

export default SignatureField
